import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { trackLogin, trackLogout } from '../utils/analytics';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios request interceptor to ensure all URLs use API_URL
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (config.url) {
          try {
            const apiUrlObj = new URL(API_URL);
            const apiHostname = apiUrlObj.hostname;
            const apiPort = apiUrlObj.port || (apiUrlObj.protocol === 'https:' ? '443' : '80');
            
            // If URL is relative (starts with /), prepend API_URL
            if (config.url.startsWith('/')) {
              config.url = `${API_URL}${config.url}`;
            }
            // If URL doesn't start with http, prepend API_URL
            else if (!config.url.startsWith('http')) {
              config.url = `${API_URL}/${config.url}`;
            }
            // If URL contains the hostname but without the correct port, fix it
            else if (apiHostname && config.url.includes(apiHostname)) {
              try {
                const urlObj = new URL(config.url);
                // urlObj.port is empty string when no port is specified
                const hasPort = urlObj.port !== '';
                const urlPort = hasPort ? urlObj.port : (urlObj.protocol === 'https:' ? '443' : '80');
                
                // If we need a non-default port and URL doesn't have it or has wrong port
                if (apiPort !== '80' && apiPort !== '443') {
                  if (!hasPort || urlPort !== apiPort) {
                    urlObj.port = apiPort;
                    config.url = urlObj.toString();
                    console.log('Fixed URL port:', config.url);
                  }
                }
              } catch (urlError) {
                // If URL parsing fails, try to fix it manually with regex
                if (config.url.includes(apiHostname) && !config.url.includes(`:${apiPort}`) && apiPort !== '80' && apiPort !== '443') {
                  // Replace http://hostname/ with http://hostname:port/
                  const escapedHostname = apiHostname.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                  config.url = config.url.replace(
                    new RegExp(`(https?://${escapedHostname})(?::\\d+)?(/.*)?$`),
                    `$1:${apiPort}$2`
                  );
                  console.log('Fixed URL port (regex):', config.url);
                }
              }
            }
          } catch (e) {
            // If URL parsing fails, try to fix it manually
            if (config.url.startsWith('/')) {
              config.url = `${API_URL}${config.url}`;
            } else if (!config.url.startsWith('http')) {
              config.url = `${API_URL}/${config.url}`;
            }
          }
        }
        
        // Don't modify form data requests - they need to stay as URLSearchParams
        // The Content-Type header should already be set correctly by the caller
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // Set up axios interceptor for 401 errors - this applies to all axios requests
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Token expired or invalid - clear auth and redirect to login
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          // Redirect to login page
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Set axios interceptor to always include token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      // Store user in localStorage for analytics
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Already handled by interceptor
        return;
      }
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      // Use FormData or URLSearchParams for OAuth2PasswordRequestForm
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      console.log('Sending login request to:', `${API_URL}/api/auth/login`);
      console.log('Login data:', { username, password: '***' });
      
      // Convert to string BEFORE passing to axios to avoid any transforms
      const formDataString = formData.toString();
      
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        formDataString,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          transformRequest: [(data) => {
            // Don't transform form data - return as-is
            return data;
          }],
        }
      );

      console.log('Login response:', response);

      if (!response.data || !response.data.access_token) {
        throw new Error('No access token received');
      }

      const token = response.data.access_token;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userData = await fetchUser();
      // Track login event after fetching user
      setTimeout(() => {
        const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
        if (currentUser) {
          trackLogin(currentUser.role || 'unknown');
        }
      }, 100);
      return response.data;
    } catch (error) {
      console.error('Login error in AuthContext:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    return response.data;
  };

  const logout = () => {
    // Track logout event before clearing user
    trackLogout();
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

